import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, XCircle, Eye, WhatsappLogo, PencilSimple,
  Clock, FileText, Package, Download, ChatCircleText,
  MagnifyingGlass, CalendarPlus, NotePencil, Plus, Trash,
  CheckSquare, Square, X, Calendar, Warning
} from "@phosphor-icons/react";
import { Button } from "../shared/Button";
import { abrirWhatsApp } from "../../utils/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (val) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(val);

export const PresupuestosPendientes = ({ adminToken, onEditar }) => {
  const [presupuestos, setPresupuestos] = useState([]);
  const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fechaVisita, setFechaVisita] = useState("");
  const [horaVisita, setHoraVisita] = useState("");
  const [nuevaNotaVisita, setNuevaNotaVisita] = useState("");
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [eliminando, setEliminando] = useState(false);
  // Calendario de disponibilidad: fecha que Andy va a confirmar/reproponer
  // (arranca con lo que pidió el cliente cuando se abre el detalle) y la
  // nota opcional para el mensaje de WhatsApp (ej. "podemos el sábado").
  const [fechaInicioForm, setFechaInicioForm] = useState("");
  const [notaFechaForm, setNotaFechaForm] = useState("");
  const [eventosAgenda, setEventosAgenda] = useState([]);
  // Teléfonos con 3+ presupuestos en 7 días sin aceptar ninguno — posible
  // uso de la app para armar presupuestos ajenos (ver GET /admin/presupuestos/sospechosos).
  const [telefonosSospechosos, setTelefonosSospechosos] = useState(new Set());

  useEffect(() => {
    cargarPresupuestos();
    cargarAgenda();
    cargarSospechosos();
  }, []);

  const cargarSospechosos = async () => {
    try {
      const res = await fetch(`${API}/admin/presupuestos/sospechosos`, {
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      const data = await res.json();
      setTelefonosSospechosos(new Set((data || []).map((s) => s.telefono)));
    } catch (err) {
      console.error("Error cargando sospechosos:", err);
    }
  };

  const cargarAgenda = async () => {
    try {
      const res = await fetch(`${API}/agenda`, {
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      setEventosAgenda(await res.json());
    } catch (err) {
      console.error("Error cargando agenda:", err);
    }
  };

  // Otros trabajos ya agendados a menos de 3 días de la fecha que se está por
  // confirmar — un aviso rápido para no chocar fechas, no un bloqueo duro
  // (Andy puede tener motivos reales para aceptar dos obras la misma semana).
  const eventosCercanos = (fechaISO, excluirPresupuestoId) => {
    if (!fechaISO) return [];
    const objetivo = new Date(fechaISO + "T00:00:00");
    return eventosAgenda.filter((ev) => {
      if (!ev.fecha || ev.presupuesto_id === excluirPresupuestoId) return false;
      const dias = Math.abs((new Date(ev.fecha + "T00:00:00") - objetivo) / 86400000);
      return dias <= 3;
    });
  };

  const cargarPresupuestos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/presupuestos`, {
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      const data = await res.json();
      setPresupuestos(data.filter(p => p.estado === "pendiente" || p.estado === "en_revision"));
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      await fetch(`${API}/admin/presupuestos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${adminToken}`
        },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      
      cargarPresupuestos();
      setPresupuestoSeleccionado(null);
      
      if (nuevoEstado === "aceptado") {
        alert("✅ Presupuesto aceptado. Ahora puedes enviar la confirmación al cliente por WhatsApp.");
      } else {
        alert("❌ Presupuesto rechazado.");
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Error al actualizar");
    }
  };

  const eliminarPresupuesto = async (id, numero) => {
    if (!window.confirm(`¿Eliminar el presupuesto ${numero}? Esta acción no se puede deshacer.`)) return;
    try {
      await fetch(`${API}/admin/presupuestos/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      setPresupuestoSeleccionado(null);
      cargarPresupuestos();
    } catch (err) {
      console.error("Error eliminando presupuesto:", err);
      alert("No se pudo eliminar el presupuesto");
    }
  };

  const toggleModoSeleccion = () => {
    setModoSeleccion((prev) => !prev);
    setSeleccionados(new Set());
  };

  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const seleccionarTodos = () => {
    setSeleccionados(new Set(presupuestos.map((p) => p.id)));
  };

  const eliminarSeleccionados = async () => {
    const cantidad = seleccionados.size;
    if (cantidad === 0) return;
    if (!window.confirm(`¿Eliminar ${cantidad} presupuesto${cantidad > 1 ? "s" : ""}? Esta acción no se puede deshacer.`)) return;
    setEliminando(true);
    try {
      await Promise.all(
        [...seleccionados].map((id) =>
          fetch(`${API}/admin/presupuestos/${id}`, {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${adminToken}` }
          })
        )
      );
    } catch (err) {
      console.error("Error eliminando presupuestos:", err);
      alert("Algunos presupuestos no se pudieron eliminar");
    } finally {
      setEliminando(false);
      setModoSeleccion(false);
      setSeleccionados(new Set());
      cargarPresupuestos();
    }
  };

  const abrirDetalle = (presupuesto) => {
    setPresupuestoSeleccionado(presupuesto);
    setFechaInicioForm(presupuesto.fecha_inicio_estimada || "");
    setNotaFechaForm("");
  };

  const cerrarDetalle = () => {
    setPresupuestoSeleccionado(null);
  };

  const enviarWhatsAppCliente = (presupuesto, forzarAceptado = false) => {
    const totalFinal = presupuesto.total_final || presupuesto.total;
    const totalMateriales = presupuesto.total_materiales || 0;
    const estadoReal = forzarAceptado ? "aceptado" : presupuesto.estado;
    const yaAceptado = ["aceptado", "en_ejecucion", "finalizado", "facturado"].includes(estadoReal);

    let mensaje = `🏠 *AAGG REFORMAS*\n\n`;

    if (estadoReal === "rechazado") {
      mensaje += `❌ *PRESUPUESTO NO APROBADO*\n\n`;
      mensaje += `Hola ${presupuesto.cliente_nombre},\n\n`;
      mensaje += `Lamentamos informarte que no podemos realizar el trabajo del presupuesto *${presupuesto.numero}* en las condiciones solicitadas.\n\n`;
      mensaje += `Si deseas, podemos ajustar el presupuesto.\n\n`;
      mensaje += `Contáctanos para más información.`;
    } else {
      // Encabezado: distinto según si ya está aceptado o todavía es una propuesta
      if (yaAceptado) {
        mensaje += `✅ *PRESUPUESTO ACEPTADO*\n\n`;
        mensaje += `Hola ${presupuesto.cliente_nombre},\n\n`;
        mensaje += `Te confirmamos que tu presupuesto *${presupuesto.numero}* ha sido aceptado.\n\n`;
      } else {
        mensaje += `📋 *DETALLE DE TU PRESUPUESTO*\n\n`;
        mensaje += `Hola ${presupuesto.cliente_nombre},\n\n`;
        mensaje += `Este es el detalle de tu presupuesto *${presupuesto.numero}*. Todavía no fue confirmado — avisanos si estás de acuerdo para coordinar la visita.\n\n`;
      }

      // Desglose de trabajos
      mensaje += `*📋 TRABAJOS A REALIZAR:*\n`;
      presupuesto.lineas?.forEach(linea => {
        // "Electrodoméstico: X" / "Grifería: X" es el precio del aparato en
        // sí (no de instalarlo) — aclarar para que no se confunda con MO.
        const esAparato = /^(electrodoméstico|grifería):/i.test(linea.trabajo_nombre || "");
        mensaje += `• ${linea.trabajo_nombre}${esAparato ? " (precio del material)" : ""}\n`;
        mensaje += `  ${linea.cantidad} ${linea.unidad} × ${formatCurrency(linea.precio_unitario)}\n`;
        if (linea.tiempo_estimado) {
          mensaje += `  ⏱️ Tiempo: ${Math.round(linea.tiempo_estimado * 100) / 100}h\n`;
        }
        mensaje += `\n`;
      });

      mensaje += `💰 *Mano de Obra: ${formatCurrency(presupuesto.total)}*\n\n`;

      if (presupuesto.tipo_presupuesto === "mo" && presupuesto.materiales_necesarios?.length > 0) {
        mensaje += `📦 *MATERIALES QUE DEBES COMPRAR:*\n`;
        mensaje += `_(No incluido en el precio de mano de obra)_\n`;
        mensaje += `_⚠️ Los precios de cada material son orientativos (+/-), pueden variar según la tienda o marca que elijas_\n\n`;

        presupuesto.materiales_necesarios.forEach(m => {
          mensaje += `• *${m.material_nombre}*\n`;
          mensaje += `  ${m.descripcion_comercial || m.cantidad + ' ' + m.material_unidad}\n`;
          if (m.marca) mensaje += `  Marca: ${m.marca}\n`;
          mensaje += `  Aprox: ${formatCurrency(m.subtotal)}\n\n`;
        });

        mensaje += `💵 Total materiales aprox: ${formatCurrency(totalMateriales)}\n\n`;
        mensaje += `💰 *TOTAL${yaAceptado ? " QUE PAGAS" : ""} (solo mano obra): ${formatCurrency(presupuesto.total)}*\n\n`;
      } else if (presupuesto.tipo_presupuesto === "mo_material") {
        mensaje += `📦 *MATERIALES INCLUIDOS (nosotros los compramos):*\n\n`;

        presupuesto.materiales_necesarios?.forEach(m => {
          mensaje += `• ${m.material_nombre}\n`;
          mensaje += `  ${m.descripcion_comercial || m.cantidad + ' ' + m.material_unidad}\n`;
        });

        mensaje += `\n💰 *TOTAL${yaAceptado ? " A PAGAR" : ""} (MO + Materiales): ${formatCurrency(totalFinal)}*\n\n`;
      }

      if (yaAceptado) {
        mensaje += `📅 *Próximos pasos:*\n`;
        mensaje += `1. Te llamaremos para confirmar fecha\n`;
        mensaje += `2. Enviaremos cronograma detallado\n`;
        if (presupuesto.tipo_presupuesto === "mo") {
          mensaje += `3. Deberás tener los materiales listos\n`;
        }
        mensaje += `\n\nGracias por confiar en nosotros! 🙏`;
      } else {
        mensaje += `\n¿Confirmamos? Respondé este mensaje o llamanos y coordinamos la visita. ¡Gracias! 🙏`;
      }
    }

    abrirWhatsApp(presupuesto.cliente_telefono, mensaje);
  };

  const actualizarPresupuesto = async (id, data) => {
    await fetch(`${API}/admin/presupuestos/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${adminToken}`
      },
      body: JSON.stringify(data)
    });
  };

  const marcarEnRevision = async (presupuesto) => {
    try {
      await actualizarPresupuesto(presupuesto.id, { estado: "en_revision" });
      const actualizado = { ...presupuesto, estado: "en_revision" };
      setPresupuestoSeleccionado(actualizado);
      cargarPresupuestos();

      const mensaje = `🏠 *AAGG REFORMAS*\n\n🔍 *TU PRESUPUESTO ESTÁ EN REVISIÓN*\n\nHola ${presupuesto.cliente_nombre},\n\nRecibimos tu presupuesto *${presupuesto.numero}* y ya lo estamos revisando. En breve te contactamos para coordinar una visita y confirmar los detalles.\n\n¡Gracias por tu paciencia! 🙏`;
      abrirWhatsApp(presupuesto.cliente_telefono, mensaje);
    } catch (err) {
      console.error("Error:", err);
      alert("Error al marcar en revisión");
    }
  };

  const agendarVisita = async (presupuesto) => {
    if (!fechaVisita) {
      alert("Elegí una fecha para la visita");
      return;
    }
    try {
      await fetch(`${API}/agenda`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          fecha: fechaVisita,
          hora: horaVisita,
          titulo: `Visita técnica - ${presupuesto.numero}`,
          cliente: presupuesto.cliente_nombre,
          telefono: presupuesto.cliente_telefono,
          direccion: presupuesto.cliente_direccion,
          notas: `Visita para revisar presupuesto ${presupuesto.numero}`,
          presupuesto_id: presupuesto.id
        })
      });
      await actualizarPresupuesto(presupuesto.id, { visita_fecha: fechaVisita, visita_hora: horaVisita });
      const actualizado = { ...presupuesto, visita_fecha: fechaVisita, visita_hora: horaVisita };
      setPresupuestoSeleccionado(actualizado);
      cargarPresupuestos();

      if (window.confirm("Visita agendada. ¿Avisar al cliente por WhatsApp?")) {
        const mensaje = `🏠 *AAGG REFORMAS*\n\n📅 *VISITA CONFIRMADA*\n\nHola ${presupuesto.cliente_nombre},\n\nConfirmamos la visita para revisar tu obra (presupuesto *${presupuesto.numero}*) el *${fechaVisita}*${horaVisita ? ` a las *${horaVisita}*` : ""}.\n\n¡Nos vemos pronto! 🙏`;
        abrirWhatsApp(presupuesto.cliente_telefono, mensaje);
      }
      setFechaVisita("");
      setHoraVisita("");
    } catch (err) {
      console.error("Error:", err);
      alert("Error al agendar la visita");
    }
  };

  const confirmarFechaInicio = async (presupuesto) => {
    if (!fechaInicioForm) {
      alert("Elegí una fecha para confirmar");
      return;
    }
    try {
      await fetch(`${API}/agenda`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
        body: JSON.stringify({
          tipo: "obra",
          fecha: fechaInicioForm,
          titulo: `Obra - ${presupuesto.numero}`,
          cliente: presupuesto.cliente_nombre,
          telefono: presupuesto.cliente_telefono,
          direccion: presupuesto.cliente_direccion,
          notas: `Inicio de obra — presupuesto ${presupuesto.numero}`,
          presupuesto_id: presupuesto.id
        })
      });
      await actualizarPresupuesto(presupuesto.id, {
        estado_fecha: "confirmada",
        fecha_inicio_estimada: fechaInicioForm
      });
      const actualizado = { ...presupuesto, estado_fecha: "confirmada", fecha_inicio_estimada: fechaInicioForm };
      setPresupuestoSeleccionado(actualizado);
      cargarPresupuestos();
      cargarAgenda();

      if (window.confirm("Fecha de inicio confirmada y agendada. ¿Avisar al cliente por WhatsApp?")) {
        const mensaje = `🏠 *AAGG REFORMAS*\n\n📅 *FECHA DE INICIO CONFIRMADA*\n\nHola ${presupuesto.cliente_nombre},\n\nConfirmamos que empezamos tu obra (presupuesto *${presupuesto.numero}*) el *${fechaInicioForm}*.${notaFechaForm ? `\n\n${notaFechaForm}` : ""}\n\n¡Nos vemos pronto! 🙏`;
        abrirWhatsApp(presupuesto.cliente_telefono, mensaje);
      }
      setNotaFechaForm("");
    } catch (err) {
      console.error("Error:", err);
      alert("Error al confirmar la fecha");
    }
  };

  const reproponerFechaInicio = async (presupuesto) => {
    if (!fechaInicioForm) {
      alert("Elegí la fecha que le querés proponer");
      return;
    }
    try {
      await actualizarPresupuesto(presupuesto.id, {
        estado_fecha: "repropuesta",
        fecha_inicio_propuesta_admin: fechaInicioForm,
        nota_fecha_admin: notaFechaForm
      });
      const actualizado = {
        ...presupuesto,
        estado_fecha: "repropuesta",
        fecha_inicio_propuesta_admin: fechaInicioForm,
        nota_fecha_admin: notaFechaForm
      };
      setPresupuestoSeleccionado(actualizado);
      cargarPresupuestos();

      if (window.confirm("Fecha repropuesta guardada. ¿Avisar al cliente por WhatsApp?")) {
        const mensaje = `🏠 *AAGG REFORMAS*\n\n📅 *SOBRE LA FECHA DE INICIO*\n\nHola ${presupuesto.cliente_nombre},\n\nPara tu obra (presupuesto *${presupuesto.numero}*) esa fecha no me queda bien — ¿te viene bien empezar el *${fechaInicioForm}*?${notaFechaForm ? `\n\n${notaFechaForm}` : ""}\n\nAvisame si te sirve. ¡Gracias! 🙏`;
        abrirWhatsApp(presupuesto.cliente_telefono, mensaje);
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Error al guardar la fecha repropuesta");
    }
  };

  const agregarNotaVisita = async (presupuesto) => {
    if (!nuevaNotaVisita.trim()) return;
    try {
      const notasActualizadas = [
        ...(presupuesto.notas_visita || []),
        { texto: nuevaNotaVisita.trim(), fecha: new Date().toISOString() }
      ];
      await actualizarPresupuesto(presupuesto.id, { notas_visita: notasActualizadas });
      setPresupuestoSeleccionado({ ...presupuesto, notas_visita: notasActualizadas });
      setNuevaNotaVisita("");
      cargarPresupuestos();
    } catch (err) {
      console.error("Error:", err);
      alert("Error al guardar la nota");
    }
  };

  const calcularTiempoTotal = (lineas) => {
    const total = lineas?.reduce((sum, l) => sum + (l.tiempo_estimado || 0), 0) || 0;
    return Math.round(total * 100) / 100;
  };

  if (loading) {
    return (
      <div className="container pt-4">
        <div className="text-center py-12">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="container pt-4 pb-24">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-h2 font-display">
          <Clock size={28} /> Presupuestos Pendientes
        </h1>
        <div className="flex items-center gap-2">
          {presupuestos.length > 0 && (
            <button
              className="icon-button"
              title={modoSeleccion ? "Cancelar selección" : "Seleccionar varios"}
              onClick={toggleModoSeleccion}
              style={modoSeleccion ? { color: "var(--error)" } : undefined}
            >
              {modoSeleccion ? <X size={22} /> : <CheckSquare size={22} />}
            </button>
          )}
          <div className="text-h3 font-bold" style={{ background: "var(--brand-accent)", color: "var(--brand-primary)", padding: "8px 16px", borderRadius: "8px" }}>
            {presupuestos.length}
          </div>
        </div>
      </div>

      {modoSeleccion && (
        <div className="card p-3 mb-4 flex items-center justify-between gap-2" style={{ background: "#FAFAF9" }}>
          <div className="text-small font-bold">
            {seleccionados.size} seleccionado{seleccionados.size !== 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={seleccionarTodos}>
              Seleccionar todos
            </Button>
            <Button
              variant="error"
              disabled={seleccionados.size === 0 || eliminando}
              onClick={eliminarSeleccionados}
            >
              <Trash size={18} /> {eliminando ? "Eliminando..." : "Eliminar"}
            </Button>
          </div>
        </div>
      )}

      {presupuestos.length === 0 ? (
        <div className="card p-8 text-center text-secondary">
          <Clock size={48} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
          <div>No hay presupuestos pendientes</div>
        </div>
      ) : (
        <div className="space-y-3">
          {presupuestos.map(presup => (
            <motion.div
              key={presup.id}
              layout
              className="card p-4 card-interactive"
              onClick={() => modoSeleccion ? toggleSeleccionado(presup.id) : abrirDetalle(presup)}
            >
              <div className="flex items-center gap-3">
                <div>
                  {modoSeleccion ? (
                    seleccionados.has(presup.id)
                      ? <CheckSquare size={32} weight="fill" style={{ color: "var(--brand-primary)" }} />
                      : <Square size={32} style={{ color: "var(--text-secondary)" }} />
                  ) : (
                    <FileText size={32} weight="fill" style={{ color: "var(--brand-primary)" }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-body">{presup.numero}</div>
                    {presup.estado === "en_revision" && (
                      <span className="text-tiny font-bold" style={{ padding: "1px 8px", borderRadius: 999, background: "#DBEAFE", color: "#1D4ED8" }}>
                        En revisión
                      </span>
                    )}
                    {telefonosSospechosos.has(presup.cliente_telefono) && (
                      <span
                        className="text-tiny font-bold"
                        title="Este teléfono generó 3+ presupuestos en 7 días sin aceptar ninguno"
                        style={{ padding: "1px 8px", borderRadius: 999, background: "#FEE2E2", color: "#B91C1C" }}
                      >
                        🚩 Repetido
                      </span>
                    )}
                  </div>
                  <div className="text-small text-secondary">{presup.cliente_nombre}</div>
                  <div className="text-tiny text-secondary">{presup.cliente_telefono}</div>
                </div>
                <div className="text-right">
                  <div className="text-h3 font-bold" style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency(presup.total_final || presup.total)}
                  </div>
                  <div className="text-tiny text-secondary">
                    {presup.lineas?.length || 0} trabajos · {calcularTiempoTotal(presup.lineas)}h
                  </div>
                  {presup.fecha_envio && (
                    <div className="text-tiny" style={{ color: "var(--warning)" }}>
                      {new Date(presup.fecha_envio).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal Detalle MEJORADO */}
      <AnimatePresence>
        {presupuestoSeleccionado && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setPresupuestoSeleccionado(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="modal-content"
              style={{ maxWidth: "800px", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-subtle">
                <div>
                  <h2 className="text-h2 font-display">{presupuestoSeleccionado.numero}</h2>
                  <div className="text-small text-secondary">
                    {presupuestoSeleccionado.tipo_presupuesto === "mo" ? "🔧 Solo Mano de Obra" : "📦 MO + Materiales"}
                    {presupuestoSeleccionado.incluye_iva ? " · Con IVA" : " · Sin IVA"}
                  </div>
                </div>
                <button
                  className="icon-button"
                  onClick={() => setPresupuestoSeleccionado(null)}
                >
                  ✕
                </button>
              </div>

              {/* Cliente */}
              <div className="card p-4 mb-4" style={{ background: "#F0F9FF" }}>
                <div className="text-label mb-2">👤 CLIENTE</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="font-bold">{presupuestoSeleccionado.cliente_nombre}</div>
                    <div className="text-small text-secondary">{presupuestoSeleccionado.cliente_telefono}</div>
                  </div>
                  <div className="text-small text-secondary">
                    {presupuestoSeleccionado.cliente_direccion}
                  </div>
                </div>
              </div>

              {/* Seguimiento: en revisión, visita, notas */}
              <div className="card p-4 mb-4" style={{ background: "#FAFAF9" }}>
                <div className="flex justify-between items-center mb-3">
                  <div className="text-label">🔍 SEGUIMIENTO</div>
                  <span
                    className="text-tiny font-bold"
                    style={{
                      padding: "2px 10px",
                      borderRadius: 999,
                      background: presupuestoSeleccionado.estado === "en_revision" ? "#DBEAFE" : "#F3F4F6",
                      color: presupuestoSeleccionado.estado === "en_revision" ? "#1D4ED8" : "#6B7280"
                    }}
                  >
                    {presupuestoSeleccionado.estado === "en_revision" ? "En revisión" : "Pendiente"}
                  </span>
                </div>

                {presupuestoSeleccionado.estado !== "en_revision" && (
                  <Button
                    variant="outline"
                    className="btn-full mb-3"
                    onClick={() => marcarEnRevision(presupuestoSeleccionado)}
                  >
                    <MagnifyingGlass size={18} /> Marcar en revisión y avisar por WhatsApp
                  </Button>
                )}

                {/* Visita */}
                {presupuestoSeleccionado.visita_fecha ? (
                  <div className="text-small mb-3" style={{ background: "#F0FDF4", padding: 10, borderRadius: 8, color: "#065F46" }}>
                    📅 Visita agendada: <strong>{presupuestoSeleccionado.visita_fecha}</strong>
                    {presupuestoSeleccionado.visita_hora && ` a las ${presupuestoSeleccionado.visita_hora}`}
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="text-tiny text-secondary mb-2">Agendar visita técnica</div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="date"
                        className="input"
                        value={fechaVisita}
                        onChange={(e) => setFechaVisita(e.target.value)}
                      />
                      <input
                        type="time"
                        className="input"
                        value={horaVisita}
                        onChange={(e) => setHoraVisita(e.target.value)}
                      />
                    </div>
                    <Button
                      variant="outline"
                      className="btn-full"
                      onClick={() => agendarVisita(presupuestoSeleccionado)}
                    >
                      <CalendarPlus size={18} /> Agendar visita
                    </Button>
                  </div>
                )}

                {/* Notas de visita */}
                <div className="text-tiny text-secondary mb-2">Notas de visita (humedades, estado del suelo, etc.)</div>
                {presupuestoSeleccionado.notas_visita?.length > 0 && (
                  <div className="mb-2">
                    {presupuestoSeleccionado.notas_visita.map((n, idx) => (
                      <div key={idx} className="text-small mb-2 pb-2 border-b border-subtle last:border-0">
                        <div>{n.texto}</div>
                        <div className="text-tiny text-secondary">{new Date(n.fecha).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input flex-1"
                    placeholder="Ej: humedad más grande de lo descrito, suelo antiguo debajo del parquet..."
                    value={nuevaNotaVisita}
                    onChange={(e) => setNuevaNotaVisita(e.target.value)}
                  />
                  <Button variant="outline" onClick={() => agregarNotaVisita(presupuestoSeleccionado)}>
                    <Plus size={18} />
                  </Button>
                </div>
              </div>

              {/* Calendario de disponibilidad: el cliente pide una fecha
                  tentativa de inicio al presupuestar, acá se revisa contra
                  la agenda y se confirma tal cual o se repropone otra —
                  ver comentario en el modelo Presupuesto (backend) para el
                  significado de cada estado_fecha. */}
              <div className="card p-4 mb-4" style={{ background: "#FAFAF9" }}>
                <div className="text-label mb-3">📅 FECHA DE INICIO</div>

                {presupuestoSeleccionado.estado_fecha === "confirmada" && (
                  <div className="text-small mb-3" style={{ background: "#F0FDF4", padding: 10, borderRadius: 8, color: "#065F46" }}>
                    🏗️ Obra confirmada para el <strong>{presupuestoSeleccionado.fecha_inicio_estimada}</strong>
                  </div>
                )}

                {presupuestoSeleccionado.estado_fecha === "repropuesta" && (
                  <div className="text-small mb-3" style={{ background: "#FFF8E1", padding: 10, borderRadius: 8, color: "#92400E" }}>
                    Le propusiste el <strong>{presupuestoSeleccionado.fecha_inicio_propuesta_admin}</strong> — esperando que confirme.
                    {presupuestoSeleccionado.nota_fecha_admin && (
                      <div className="text-tiny mt-1">"{presupuestoSeleccionado.nota_fecha_admin}"</div>
                    )}
                  </div>
                )}

                {presupuestoSeleccionado.estado_fecha === "pendiente" && (
                  <div className="text-small mb-3" style={{ background: "#EFF6FF", padding: 10, borderRadius: 8, color: "#1D4ED8" }}>
                    El cliente pidió empezar el <strong>{presupuestoSeleccionado.fecha_inicio_estimada}</strong>
                  </div>
                )}

                {!presupuestoSeleccionado.estado_fecha && (
                  <div className="text-tiny text-secondary mb-3">El cliente no pidió una fecha — podés agendar una vos.</div>
                )}

                {presupuestoSeleccionado.estado_fecha !== "confirmada" && (
                  <>
                    {eventosCercanos(fechaInicioForm, presupuestoSeleccionado.id).length > 0 && (
                      <div className="text-tiny mb-2" style={{ color: "var(--warning)", display: "flex", alignItems: "flex-start", gap: 6 }}>
                        <Warning size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>
                          Ya tenés agendado cerca de esa fecha: {eventosCercanos(fechaInicioForm, presupuestoSeleccionado.id).map(ev => `${ev.titulo || ev.cliente} (${ev.fecha})`).join(", ")}
                        </span>
                      </div>
                    )}
                    <div className="mb-2">
                      <input
                        type="date"
                        className="input"
                        style={{ width: "100%" }}
                        value={fechaInicioForm}
                        onChange={(e) => setFechaInicioForm(e.target.value)}
                      />
                    </div>
                    <input
                      type="text"
                      className="input mb-2"
                      style={{ width: "100%" }}
                      placeholder="Nota opcional para el WhatsApp (ej: podemos el sábado, o hasta tarde si no molesta a vecinos)"
                      value={notaFechaForm}
                      onChange={(e) => setNotaFechaForm(e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="success" onClick={() => confirmarFechaInicio(presupuestoSeleccionado)}>
                        <CheckCircle size={18} /> Confirmar esta fecha
                      </Button>
                      <Button variant="outline" onClick={() => reproponerFechaInicio(presupuestoSeleccionado)}>
                        <Calendar size={18} /> Proponer otra
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Trabajos con tiempo */}
              <div className="card p-4 mb-4">
                <div className="text-label mb-3">📋 TRABAJOS A REALIZAR</div>
                {presupuestoSeleccionado.lineas?.map((linea, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b border-subtle last:border-0">
                    <div className="flex justify-between mb-1">
                      <div className="font-bold">
                        {linea.trabajo_nombre}
                        {/^(electrodoméstico|grifería):/i.test(linea.trabajo_nombre || "") && (
                          <span className="text-small text-secondary font-normal"> (precio del material)</span>
                        )}
                      </div>
                      <div className="font-bold">{formatCurrency(linea.subtotal)}</div>
                    </div>
                    <div className="flex justify-between text-small text-secondary">
                      <div>{linea.cantidad} {linea.unidad} × {formatCurrency(linea.precio_unitario)}</div>
                      {linea.tiempo_estimado && (
                        <div>⏱️ {Math.round(linea.tiempo_estimado * 100) / 100}h</div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex justify-between pt-3 border-t-2 border-primary">
                  <div className="font-bold">⏱️ Tiempo total estimado:</div>
                  <div className="font-bold text-h3" style={{ color: "var(--brand-accent)" }}>
                    {calcularTiempoTotal(presupuestoSeleccionado.lineas)}h
                  </div>
                </div>
              </div>

              {/* Materiales separados */}
              {presupuestoSeleccionado.materiales_necesarios?.length > 0 && (
                <>
                  {presupuestoSeleccionado.tipo_presupuesto === "mo" ? (
                    <div className="card p-4 mb-4" style={{ background: "#FFF8E1", border: "2px solid #F59E0B" }}>
                      <div className="text-label mb-2" style={{ color: "#92400E" }}>
                        📦 MATERIALES QUE COMPRA EL CLIENTE
                      </div>
                      <div className="text-tiny mb-3" style={{ color: "#78350F" }}>
                        No incluido en el precio. El cliente debe comprar estos materiales.
                      </div>
                      {presupuestoSeleccionado.materiales_necesarios.map((mat, idx) => (
                        <div key={idx} className="mb-2 pb-2 border-b border-subtle last:border-0">
                          <div className="flex justify-between">
                            <div className="font-bold text-small">{mat.material_nombre}</div>
                            <div className="font-bold">{formatCurrency(mat.subtotal)}</div>
                          </div>
                          <div className="text-tiny text-secondary">
                            {mat.descripcion_comercial || `${mat.cantidad} ${mat.material_unidad}`}
                          </div>
                          {mat.marca && (
                            <div className="text-tiny" style={{ color: "#92400E" }}>
                              Marca: {mat.marca} {mat.referencia && `· Ref: ${mat.referencia}`}
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="border-t-2 pt-2 mt-2 font-bold flex justify-between" style={{ borderColor: "#F59E0B" }}>
                        <span>Total Materiales (orientativo):</span>
                        <span>{formatCurrency(presupuestoSeleccionado.total_materiales || 0)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="card p-4 mb-4" style={{ background: "#F0FDF4", border: "2px solid #10B981" }}>
                      <div className="text-label mb-2" style={{ color: "#065F46" }}>
                        📦 MATERIALES QUE YO USO EN OBRA
                      </div>
                      <div className="text-tiny mb-3" style={{ color: "#047857" }}>
                        Incluido en el precio. Nosotros compramos todos los materiales.
                      </div>
                      {presupuestoSeleccionado.materiales_necesarios.map((mat, idx) => (
                        <div key={idx} className="mb-2 pb-2 border-b border-subtle last:border-0">
                          <div className="flex justify-between">
                            <div className="font-bold text-small">{mat.material_nombre}</div>
                            <div className="font-bold">{formatCurrency(mat.subtotal)}</div>
                          </div>
                          <div className="text-tiny text-secondary">
                            {mat.descripcion_comercial || `${mat.cantidad} ${mat.material_unidad}`}
                          </div>
                        </div>
                      ))}
                      <div className="border-t-2 pt-2 mt-2 font-bold flex justify-between" style={{ borderColor: "#10B981" }}>
                        <span>Total Materiales:</span>
                        <span>{formatCurrency(presupuestoSeleccionado.total_materiales || 0)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Totales */}
              <div className="card p-4 mb-4" style={{ background: "var(--brand-primary)", color: "white" }}>
                <div className="flex justify-between mb-2">
                  <span>💰 Mano de Obra:</span>
                  <span className="font-bold">{formatCurrency(presupuestoSeleccionado.total)}</span>
                </div>
                {presupuestoSeleccionado.tipo_presupuesto === "mo_material" && presupuestoSeleccionado.total_materiales > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>📦 Materiales:</span>
                    <span className="font-bold">{formatCurrency(presupuestoSeleccionado.total_materiales)}</span>
                  </div>
                )}
                <div className="border-t-2 pt-2 mt-2 flex justify-between text-h2">
                  <span>TOTAL {presupuestoSeleccionado.tipo_presupuesto === "mo" ? "(Solo MO)" : "COMPLETO"}:</span>
                  <span className="font-bold" style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency(presupuestoSeleccionado.total_final || presupuestoSeleccionado.total)}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="success"
                    onClick={() => {
                      if (window.confirm("¿Aceptar este presupuesto?\n\nSe enviará confirmación al cliente.")) {
                        cambiarEstado(presupuestoSeleccionado.id, "aceptado");
                        enviarWhatsAppCliente(presupuestoSeleccionado, true);
                      }
                    }}
                  >
                    <CheckCircle size={20} /> Aceptar y avisar
                  </Button>
                  <Button
                    variant="error"
                    onClick={() => {
                      if (window.confirm("¿Rechazar este presupuesto?")) {
                        cambiarEstado(presupuestoSeleccionado.id, "rechazado");
                      }
                    }}
                  >
                    <XCircle size={20} /> Rechazar
                  </Button>
                </div>

                <Button
                  variant="whatsapp"
                  className="btn-full"
                  onClick={() => enviarWhatsAppCliente(presupuestoSeleccionado)}
                >
                  <WhatsappLogo size={20} /> Enviar detalles por WhatsApp
                </Button>

                {onEditar && (
                  <Button
                    variant="outline"
                    className="btn-full"
                    onClick={() => onEditar(presupuestoSeleccionado)}
                  >
                    <PencilSimple size={20} /> Editar presupuesto (trabajos y materiales)
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="btn-full"
                  onClick={() => window.open(`${API}/presupuestos/${presupuestoSeleccionado.id}/pdf?token_acceso=${encodeURIComponent(presupuestoSeleccionado.token_acceso || "")}&admin_token=${encodeURIComponent(adminToken || "")}`, "_blank")}
                >
                  <Download size={20} /> Ver presupuesto PDF
                </Button>

                <Button
                  variant="outline"
                  className="btn-full"
                  style={{ color: "var(--error)", borderColor: "var(--error)" }}
                  onClick={() => eliminarPresupuesto(presupuestoSeleccionado.id, presupuestoSeleccionado.numero)}
                >
                  <Trash size={20} /> Eliminar presupuesto
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
